import { Descendant, Element as SlateElement, Node, Text } from 'slate';
import { CustomElement } from '../utils/element-renderer';

/**
 * Types of messages supported in the preview
 */
export type MessageType = 'text' | 'media' | 'media_card';

/**
 * Preview message structure for rendering in the message preview component
 */
export interface SerializedMessage {
  type: MessageType;
  content: {
    text?: string;
    url?: string;
    caption?: string;
    contactData?: {
      name: string;
      phoneNumbers: string[];
      emails: string[];
    };
  };
}

/**
 * Result of the preview serialization process
 */
export interface SerializedContent {
  messages: SerializedMessage[];
}

/**
 * Type guard to check if a node is a text node
 */
function isTextNode(node: Descendant): node is Text {
  return !SlateElement.isElement(node) && 'text' in node;
}

/**
 * Type guard for custom element types
 */
function isCustomElement<T extends CustomElement['type']>(
  node: Descendant,
  type: T
): node is Extract<CustomElement, { type: T }> {
  return SlateElement.isElement(node) && node.type === type;
}

/**
 * Serializes Slate editor nodes for the message preview component.
 *
 * This preview serializer:
 * 1. Processes each node in the document
 * 2. Preserves variable syntax with handlebars
 * 3. Formats content for visual preview
 * 4. Handles media attachments and contact cards
 *
 * Note: This is specifically for preview purposes. Production serialization
 * for the backend API is handled separately.
 *
 * @param nodes - Array of Slate node descendants from the editor
 * @returns Serialized content formatted for the message preview
 */
export function serializeForPreview(nodes: Descendant[]): SerializedContent {
  const result: SerializedContent = { messages: [] };
  let currentTextContent = '';

  // Function to flush accumulated text into a message
  const flushTextContent = () => {
    if (currentTextContent) {
      result.messages.push({
        type: 'text',
        content: { text: currentTextContent.trimEnd() }
      });
      currentTextContent = '';
    }
  };

  // Process a single node and its children
  const processNode = (node: Descendant): void => {
    // Handle text nodes directly
    if (isTextNode(node)) {
      currentTextContent += node.text;
      return;
    }

    // Handle element nodes based on type
    if (SlateElement.isElement(node)) {
      if (isCustomElement(node, 'paragraph')) {
        // Process paragraph children and add newlines
        node.children.forEach(processNode);
        // Only add newline if there's content
        if (currentTextContent) {
          currentTextContent += '\n';
        }
      } else if (isCustomElement(node, 'image')) {
        // Images become separate media messages
        flushTextContent();
        result.messages.push({
          type: 'media',
          content: { url: node.url }
        });
      } else if (isCustomElement(node, 'variable')) {
        // Preserve variable syntax with handlebars and default if it exists
        currentTextContent += node.defaultValue
          ? `{{${node.variableName} | default = "${node.defaultValue}"}}`
          : `{{${node.variableName}}}`;
      } else if (isCustomElement(node, 'shortlink')) {
        // Shortlinks are included as plain text URLs
        currentTextContent += node.shortUrl;
      } else if (isCustomElement(node, 'stop-message')) {
        // Stop messages are included as text
        currentTextContent += node.messageText;
      } else if (isCustomElement(node, 'contact-card')) {
        // Contact cards become separate attachments
        flushTextContent();
        const { name, phoneNumbers, emails } = node.data;

        result.messages.push({
          type: 'media_card',
          content: {
            url: node.data.vcfString ?? '',
            caption: `Contact: ${name}`,
            contactData: {
              name,
              phoneNumbers: phoneNumbers.map((p) => p.number),
              emails: emails.map((e) => e.address)
            }
          }
        });
      } else {
        // For unknown element types, try to extract text using Node.string
        currentTextContent += Node.string(node);
      }
    }
  };

  // Process all nodes
  nodes.forEach(processNode);

  // Flush any remaining text content
  flushTextContent();

  // If there are no messages, add an empty text message
  if (result.messages.length === 0) {
    result.messages.push({
      type: 'text',
      content: { text: '' }
    });
  }

  return result;
}
